import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Clock, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/os-patch`;

/**
 * SyncScheduleCard
 * ----------------
 * Lets each tenant pick the UTC hour + minute when their nightly Intune
 * sync runs, plus opt in to including the Apps Management module in the
 * nightly run. Persists to the tenant's `settings.sync_schedule` doc;
 * the backend `nightly_sync_scheduler` reads this on every minute-tick
 * so changes take effect within ~60 seconds without a restart.
 *
 * Default = 02:00 UTC, apps_enabled=false (preserves legacy behaviour).
 */
export default function SyncScheduleCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(0);
  const [appsEnabled, setAppsEnabled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/sync-schedule`);
        setHour(res.data?.hour_utc ?? 2);
        setMinute(res.data?.minute_utc ?? 0);
        setAppsEnabled(!!res.data?.apps_enabled);
      } catch (e) {
        console.error('Failed to load sync schedule', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/sync-schedule`, {
        hour_utc: Number(hour),
        minute_utc: Number(minute),
        apps_enabled: appsEnabled,
      });
      toast.success(
        `Nightly sync scheduled for ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} UTC`
        + (appsEnabled ? ' (incl. Apps Management)' : '')
      );
    } catch (e) {
      toast.error('Could not save schedule: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // Render local-time preview so non-UTC operators understand when this fires.
  const localPreview = (() => {
    try {
      const d = new Date();
      d.setUTCHours(hour, minute, 0, 0);
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  })();

  return (
    <Card data-testid="sync-schedule-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Clock className="h-5 w-5" />
          Nightly Sync Schedule
        </CardTitle>
        <CardDescription>
          Choose what time of day this tenant's nightly Intune sync runs.
          Changes apply on the next 60-second scheduler tick — no restart needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading current schedule…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label htmlFor="sync-hour" className="text-sm">Hour (UTC)</Label>
                <select
                  id="sync-hour"
                  data-testid="sync-hour-select"
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                  className="block w-24 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sync-minute" className="text-sm">Minute (UTC)</Label>
                <select
                  id="sync-minute"
                  data-testid="sync-minute-select"
                  value={minute}
                  onChange={(e) => setMinute(Number(e.target.value))}
                  className="block w-24 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-muted-foreground pb-2">
                {localPreview && (
                  <>
                    ~{localPreview} in your local timezone
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="space-y-0.5 mr-4">
                <Label htmlFor="apps-enabled" className="text-sm font-medium">
                  Include Apps Management sync
                </Label>
                <p className="text-xs text-muted-foreground">
                  Also sync Detected Apps from Intune as part of the nightly
                  run. Uses app-only Graph credentials — no user must be
                  logged in.
                </p>
              </div>
              <Switch
                id="apps-enabled"
                data-testid="sync-apps-enabled-switch"
                checked={appsEnabled}
                onCheckedChange={setAppsEnabled}
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-sync-schedule-btn"
              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold border-none"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Save Schedule
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
